'use client';
import {ExtraInfos} from '@/services/types';
import {useEffect, useState} from 'react';
import {cn} from '@/lib/utils';
import {IExtraQuantitySelectDictionary} from '@/dictionaries/components/extraQuantitySelect/types';
import useCurrency from '@/hooks/useCurrency';

type Props = {
  className?: string;
  id: string;
  onChange: (quantity: number) => void;
  extra: ExtraInfos;
  lang: string;
  index: number;
  dict: IExtraQuantitySelectDictionary;
  currencySymbol: string;
  selectedQuantity?: number;
};

export default function ExtraQuantitySelect({
  className,
  id,
  onChange,
  extra,
  lang,
  index,
  dict,
  currencySymbol,
  selectedQuantity,
}: Props) {
  const [quantity, setQuantity] = useState(0);
  const [isChecked, setIsChecked] = useState(false);

  const {applyCurrency} = useCurrency();

  const {
    label,
    hasQuantity,
    price,
    isFlatPrice,
    maxQuantity,
    freeQuantity,
    minNights,
    payInVilla,
    noteNumber,
  } = extra;

  const formatedPrice = applyCurrency(price);

  function handleAdd() {
    setQuantity(quantity + 1);
    onChange(quantity + 1);
  }

  function handleRemove() {
    setQuantity(quantity - 1);
    onChange(quantity - 1);
    if (!isChecked) {
      setIsChecked(true);
    }
  }

  useEffect(() => {
    if (quantity === 0) {
      if (isChecked) {
        setIsChecked(false);
      }
    }

    if (quantity > 0 && !isChecked) {
      setIsChecked(true);
    }
  }, [quantity]);

  useEffect(() => {
    if (!isChecked) {
      if (quantity > 0) {
        setQuantity(0);
        onChange(0);
      }
    } else {
      if (quantity === 0) {
        setQuantity(1);
        onChange(1);
      }
    }
  }, [isChecked]);

  useEffect(() => {
    setQuantity(selectedQuantity || 0);
  }, [selectedQuantity]);

  return (
    <div className={cn('flex flex-wrap items-center mt-[0.625rem]', className)}>
      <div className="mt-[0.625rem] flex-[0_0_auto] w-full md:w-[41.66667%]">
        <input
          onChange={() => setIsChecked((prev) => !prev)}
          checked={isChecked}
          id={id}
          className="hidden"
          type="checkbox"
        />
        <label
          className="cursor-pointer h-auto align-baseline flex mr-auto ml-0 justify-start items-center relative text-gray-700 leading-[1.8] text-sm"
          htmlFor={id}>
          <span
            className={cn(
              'w-[25px] max-w-[25px] flex-[1_0_25px] h-[25px] bg-gray-100 border border-gray-200 rounded-[50px] mr-[10px] cursor-pointer',
              {
                'bg-blue': isChecked,
              },
            )}
          />
          <div>
            <div className={`${isChecked && 'text-graphite'}`}>{label}</div>
          </div>
        </label>
      </div>
      <div className="mt-[0.625rem] w-[66.66667%] md:w-[33.33333%] flex-[0_0_auto] pl-[35px] md:pl-4 md:order-3 ">
        {freeQuantity > 0 ? (
          <span>
            {quantity <= freeQuantity
              ? dict.free
              : `${currencySymbol} ${
                  formatedPrice * (quantity - freeQuantity)
                } ${!isFlatPrice ? dict.perDay : ''}`}
            {(minNights > 0 || payInVilla) && (
              <sup className="text-xs text-blue">{noteNumber}</sup>
            )}
          </span>
        ) : (
          <span>
            {`${currencySymbol} ${
              quantity > 0 ? formatedPrice * quantity : formatedPrice
            } ${!isFlatPrice ? dict.perDay : ''}`}{' '}
            {(minNights > 0 || payInVilla) && (
              <sup className="text-xs text-blue">{noteNumber}</sup>
            )}
          </span>
        )}
      </div>

      <div className="mt-[0.625rem] w-[33.33333%] md:w-1/4 flex-[0_0_auto] relative flex flex-wrap items-center justify-center">
        {hasQuantity && (
          <div className="absolute right-[5px] top-[50%] translate-y-[-50%] flex flex-wrap items-start justify-between">
            <button
              type="button"
              id={`${id}-extra-decrement`}
              aria-label={`Decrement ${label} extra`}
              disabled={quantity < 1}
              onClick={handleRemove}
              className="w-6 h-6 text-gray-500 rounded-full border border-gray-500 cursor-pointer flex justify-center items-center disabled:cursor-default disabled:opacity-40">
              <svg
                className="w-7 h-7 fill-current"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 -960 960 960">
                <path d="M200-440v-80h560v80H200Z" />
              </svg>
            </button>
            <span className="mx-2 max-w-[13px] w-[13px]">{quantity}</span>
            <button
              type="button"
              id={`${id}-extra-increment`}
              aria-label={`Increment ${label} extra`}
              disabled={quantity === maxQuantity}
              onClick={handleAdd}
              className="w-6 h-6 text-gray-500 rounded-full border border-gray-500 cursor-pointer flex justify-center items-center disabled:cursor-default disabled:opacity-40">
              <svg
                className="w-7 h-7 fill-current"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 -960 960 960">
                <path d="M440-440H200v-80h240v-240h80v240h240v80H520v240h-80v-240Z" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
